import { translateField } from "@cdv/jazz-i18n";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactElement, useContext, useEffect, useRef } from "react";
import AppBar from "../ui/AppBar";
import { Resource } from "../utils";
import { useNavigate } from "react-router";
import { ActionType, AppContext } from "@cdv/jazz-ui";
import { useSnackbar } from "notistack";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function Contact(): ReactElement {
  const sx = { color: "#00000099", mt: "15px" };
  const { enqueueSnackbar } = useSnackbar();

  const recaptchaRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const renderRecaptcha = () => {
      if (!recaptchaRef.current || !window.grecaptcha || widgetId.current !== null) {
        return;
      }

      widgetId.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: "6LfCih0sAAAAAKRRM13zN2ToCX1Egcrcg6SKawn_",
        size: "invisible",
        badge: "inline",
        callback: async (token: string) => {
          if (!formRef.current) return;

          const data = new FormData(formRef.current);

          // reCAPTCHA token presne pod názvom, ktorý očakáva API
          data.set("g-recaptcha-response", token);

          // ďalšie hodnoty, ktoré očakáva /send-cms
          data.set("to", "veronika.kureckova@cdv.cz");
          data.set("subject", "Kontaktní formulář | Stressoff");

          try {
            const response = await fetch("https://email-api.cdv.cz/send-cms", {
              method: "POST",
              body: data,
            });

            if (!response.ok) {
              console.log("test");
              enqueueSnackbar("Chyba při odesílání formuláře.", { variant: "error" });
            } else {
              enqueueSnackbar("Formulář byl úspěšně odeslán.", { variant: "success" });
            }

            const result = await response.json();

            formRef.current.reset();

            if (widgetId.current !== null) {
              window.grecaptcha.reset(widgetId.current);
            }
          } catch (error) {
            enqueueSnackbar("Chyba při odesílání formuláře.", { variant: "error" });

            if (widgetId.current !== null) {
              window.grecaptcha.reset(widgetId.current);
            }
          }
        },
      });
    };

    renderRecaptcha();
  }, []);

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (widgetId.current === null) {
      console.error("reCAPTCHA ešte nie je pripravená.");
      return;
    }

    window.grecaptcha.execute(widgetId.current);
  };

  return (
    <>
      <AppBar title={translateField(Resource.Info, "contact")} />

      <Card sx={{ mx: "16px", mt: "0 !important", borderRadius: 0, boxShadow: "none" }}>
        <CardContent sx={{ mt: 0 }}>
          <Stack sx={{ justifyContent: "center", mt: "0 !important" }} spacing={2}>
            <Stack sx={{ alignItems: "center" }}>
              <img src="/assets/ilustr_INFO_04.svg" style={{ height: "164px" }} />
            </Stack>
            <Typography variant="h5" align="center">
              Garanti projektu
            </Typography>

            <Typography sx={sx}>
              Odbornou garanci projektu zajišťují psychologové Centra dopravního výzkumu, v.v.i.
            </Typography>

            <Card>
              <CardContent>
                <Stack direction={"row"} sx={{ alignItems: "center", gap: "16px" }}>
                  <Avatar sx={{ height: "24px", width: "24px" }} />{" "}
                  <Box>Mgr. Veronika Kurečková</Box>{" "}
                </Stack>

                <Typography align="center" sx={{ mt: "16px" }}>
                  <a
                    style={{ textDecoration: "none", color: "#00000099", fontWeight: "bold" }}
                    href="tel:+420 601 355 641"
                  >
                    +420 601 355 641
                  </a>
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="h5" align="center">
              Napište nám
            </Typography>

            <Box>
              <form method="POST" ref={formRef} onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField label="Jméno a Příjmení" name="name" required />
                  <TextField label="E-mail" name="email" type="email" required />
                  <TextField label="Telefon" name="phone" type="tel" />
                  <TextField label="Zpráva" name="message" multiline rows={4} />
                  <div ref={recaptchaRef} />
                  <Button variant="contained" color="warning" type="submit" fullWidth size="large">
                    Odeslat
                  </Button>
                </Stack>
              </form>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </>
  );
}
